import type { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { useRef } from "react";

type SessionStatus = "authenticated" | "loading" | "unauthenticated";

type SessionSnapshot = {
  data: Session | null;
  status: SessionStatus;
};

export function resolveStableSession(
  sessionState: SessionSnapshot,
  lastSession: Session | null,
  offline: boolean
): SessionSnapshot {
  if (sessionState.status === "unauthenticated" && offline && lastSession) {
    return { data: lastSession, status: "authenticated" };
  }

  return sessionState;
}

/**
 * Sticky session for authenticated in-app UI while offline.
 * Use instead of `useSession()` for shell chrome, providers, and logged-in surfaces.
 * Keep raw `useSession()` for login gates, Google One Tap, and public CTAs.
 * @see docs/OFFLINE_UX.md
 */
export function useStableSession() {
  const sessionState = useSession();
  const lastSession = useRef<Session | null>(null);

  if (sessionState.data) {
    lastSession.current = sessionState.data;
  }

  const offline = typeof navigator !== "undefined" && !navigator.onLine;

  const resolved = resolveStableSession(
    sessionState,
    lastSession.current,
    offline
  );

  if (
    resolved.data !== sessionState.data ||
    resolved.status !== sessionState.status
  ) {
    return { ...sessionState, ...resolved };
  }

  return sessionState;
}
